import { useState } from "react";
import { useTrial } from "../../context/TrialContext";
import { useAuth } from "../../context/AuthContext";
import { Rocket, Check, ChevronRight, X } from "lucide-react";

const STEPS = [
  { key: "account_created", label: "Create your account", time: "Just now" },
  { key: "first_department", label: "Create your first department", time: "~2 minutes" },
  { key: "first_bia", label: "Run your first BIA process", time: "~5 minutes" },
  { key: "invited_member", label: "Invite a team member", time: "~1 minute" },
  { key: "reviewed_dashboard", label: "Review your KPI dashboard", time: "~1 minute" },
];

export const OnboardingChecklist = () => {
  const { isTrial, checklist, completeChecklistStep } = useTrial();
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);

  if (!isTrial || !checklist || dismissed) return null;
  // Platform admin / webadmin does not need the trial checklist
  if (user?.role === "admin" || user?.is_platform_admin) return null;

  const completedCount = STEPS.filter((s) => checklist[s.key]).length;
  const allDone = completedCount === STEPS.length;
  if (allDone) return null;

  const activeIndex = STEPS.findIndex((s) => !checklist[s.key]);
  const progress = (completedCount / STEPS.length) * 100;

  return (
    <div
      data-testid="onboarding-checklist-panel"
      className="fixed bottom-4 right-4 w-80 bg-white rounded-xl shadow-lg border border-cyan-200 z-50 overflow-hidden pointer-events-none"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Rocket className="w-4 h-4" />
          <span className="font-bold text-sm">Get Started with ResiliencyHUB</span>
        </div>
        <button
          data-testid="checklist-dismiss-btn"
          onClick={() => setDismissed(true)}
          className="text-white/70 hover:text-white pointer-events-auto"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Steps */}
      <div className="p-4 space-y-2">
        {STEPS.map((step, i) => {
          const done = checklist[step.key];
          const isActive = i === activeIndex;

          return (
            <button
              key={step.key}
              data-testid={`checklist-step-${step.key}`}
              onClick={() => {
                if (!done && isActive) completeChecklistStep(step.key);
              }}
              className={`w-full text-left flex items-start gap-3 p-2.5 rounded-lg transition text-sm ${
                isActive ? "pointer-events-auto " : ""
              }${
                done
                  ? "bg-emerald-50 border border-emerald-200"
                  : isActive
                  ? "bg-cyan-50 border-2 border-cyan-400"
                  : "bg-slate-50 border border-slate-200 opacity-60"
              }`}
            >
              <div
                className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  done
                    ? "bg-emerald-500"
                    : isActive
                    ? "bg-cyan-500"
                    : "bg-slate-300"
                }`}
              >
                {done ? (
                  <Check className="w-3 h-3 text-white" />
                ) : isActive ? (
                  <ChevronRight className="w-3 h-3 text-white" />
                ) : (
                  <span className="text-white text-[10px] font-bold">{i + 1}</span>
                )}
              </div>
              <div>
                <div className={`font-semibold ${done ? "text-emerald-800" : isActive ? "text-cyan-800" : "text-slate-600"}`}>
                  {step.label}
                </div>
                <div className={`text-xs ${done ? "text-emerald-600" : isActive ? "text-cyan-600" : "text-slate-400"}`}>
                  {done ? "Completed" : step.time}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Progress */}
      <div className="px-4 pb-4">
        <div className="bg-slate-50 rounded-lg p-3 text-center">
          <div className="text-xs text-slate-500 mb-1">Setup Progress</div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className="bg-cyan-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="text-xs text-slate-400 mt-1">
            {completedCount} of {STEPS.length} completed
          </div>
        </div>
      </div>
    </div>
  );
};
